import { autorun, comparer, reaction } from 'mobx';
import { observer } from 'mobx-react';
import React, { useEffect, useLayoutEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    fileCabinetLocationFrom,
    fileCabinetLocationTo,
    fileCabinetSameLocation,
} from '../../services/file-cabinet/file-cabinet.location';
import { fileCabinetMergeFilters } from '../../services/file-cabinet/file-cabinet.query';
import type { FileCabinetRecord, FileCabinetViewKey } from '../../services/file-cabinet/file-cabinet.types';
import { FILE_CABINET_VIEWS } from '../../services/file-cabinet/file-cabinet.views';
import { FileCabinetStore } from '../../stores/file-cabinet/file-cabinet.store';
import { BaseButton } from '../base/button/button';
import { DataTablePresenter } from '../data-table/data-table.presenter';
import { FileCabinetDrawer } from './file-cabinet-drawer';
import { FileCabinetNav } from './file-cabinet-nav';
import { createFileCabinetPresenter } from './file-cabinet-presenter';
import { FileCabinetTable } from './file-cabinet-table';
import * as S from './file-cabinet.styles';

export interface IFileCabinetScreenProps {
    /** The app's File Cabinet store: its data source (provider or support API) decides everything app-specific. */
    store: FileCabinetStore;
    /**
     * The app's own header actions — Upload. Each app already has an upload flow (its dialog, its
     * batch-upload store, its demo guard), so the screen hosts it rather than growing a third one.
     */
    headerActions?: React.ReactNode;
}

/**
 * The File Cabinet (FE-225): All Documents, and the mail and faxes that carried them, in one screen
 * both apps mount. Design: Communications Hub bundle, "File Cabinet v2".
 *
 * One table presenter per view, so each keeps its own sort, columns and Filters-panel entries. The
 * page keeps them in step with the store: rows and total flow store → table, and the store's search
 * box and type shortcut are merged into the active table's filters — which is what sends the table
 * back to page one and makes it ask for data.
 *
 * The tab, direction and open record are in the URL (`file-cabinet.location.ts`) — never the search.
 */
export const FileCabinetScreen = observer(({ store, headerActions }: IFileCabinetScreenProps): React.ReactElement => {
    const presenters = useMemo(() => {
        const views = Object.keys(FILE_CABINET_VIEWS) as FileCabinetViewKey[];
        return new Map<FileCabinetViewKey, DataTablePresenter<FileCabinetRecord>>(
            views.map(view => [
                view,
                createFileCabinetPresenter(view, store.dataSource.app, store.dataSource.documentTypes, store.dataSource.capabilities, {
                    attachments: () => store.attachments,
                    open: store.openRecord,
                }),
            ]),
        );
    }, [store]);
    const presenter = presenters.get(store.view) as DataTablePresenter<FileCabinetRecord>;

    const [params, setParams] = useSearchParams();
    // URL → store: on arrival and whenever the URL changes from outside (a pasted link, back/forward).
    // A layout effect, so a linked tab is chosen before the default tab's table sends its first fetch
    // (debounced; the unmounted table drops it).
    useLayoutEffect(() => {
        const target = fileCabinetLocationFrom(params);
        if (!fileCabinetSameLocation(target, store.location)) {
            void store.applyLocation(target);
        }
    }, [params, store]);
    // store → URL. Replaced, not pushed: every tab click is not a history entry.
    useEffect(
        () =>
            reaction(
                () => store.location,
                location => setParams(previous => fileCabinetLocationTo(previous, location), { replace: true }),
                { equals: comparer.structural },
            ),
        [store, setParams],
    );

    useEffect(() => {
        void store.loadCounts();
        const disposers = [
            autorun(() => {
                const active = presenters.get(store.view);
                active?.onDataUpdate(store.rows);
                active?.onDataTotalChange(store.total ?? 0);
            }),
            // The type shortcut and the Filters panel's "Document type" are one choice. The API ORs a
            // repeated `filterDocumentType`, so "W-9" plus "not CMS-1500" would return almost every
            // document under filters that look applied. Picking a shortcut drops the panel entry;
            // adding a panel entry clears the shortcut.
            reaction(
                () => store.documentType,
                type => {
                    const docs = presenters.get('docs');
                    if (type && docs) {
                        docs.updateFilters(docs.filters.filter(f => f.hidden || f.filterId !== 'filterDocumentType'));
                    }
                },
            ),
            reaction(
                () => presenters.get('docs')?.validFilters.some(f => !f.hidden && f.filterId === 'filterDocumentType'),
                panelHasType => {
                    if (panelHasType && store.documentType) {
                        store.setDocumentType(undefined);
                    }
                },
            ),
            reaction(
                () => ({ view: store.view, owned: store.ownFilters }),
                ({ view, owned }) => {
                    const active = presenters.get(view);
                    active?.updateFilters(fileCabinetMergeFilters(active.filters, owned));
                },
                { fireImmediately: true, equals: comparer.structural },
            ),
        ];
        return (): void => disposers.forEach(dispose => dispose());
    }, [store, presenters]);

    return (
        <S.Wrapper>
            <S.Header>
                <S.Title>File Cabinet</S.Title>
                {headerActions}
            </S.Header>
            <FileCabinetNav store={store} />
            {store.error && !store.error.forbidden && (
                <S.PageBanner
                    tone="danger"
                    title="This list could not be loaded"
                    action={
                        <BaseButton variant="outlined" size="small" onClick={(): void => void store.reload()}>
                            Retry
                        </BaseButton>
                    }
                >
                    {store.error.message} There may be records we couldn't show.
                </S.PageBanner>
            )}
            {store.linkError && (
                <S.PageBanner
                    tone="warning"
                    title="The link did not open a record"
                    action={
                        <BaseButton variant="outlined" size="small" onClick={store.dismissLinkError}>
                            Dismiss
                        </BaseButton>
                    }
                >
                    {store.linkError}
                </S.PageBanner>
            )}
            <S.Panel>
                {/* Keyed by view: each table mounts with its own presenter and asks for its own first page. */}
                <FileCabinetTable key={store.view} store={store} view={store.view} presenter={presenter} />
            </S.Panel>
            <FileCabinetDrawer store={store} />
        </S.Wrapper>
    );
});
